import React, { useMemo } from "react";

import GroupedFilterDropdown, {
  GroupedFilterEntry,
} from "../GroupedFilterDropdown";
import { AccountOption } from "../../../types";

type Props = {
  values: string[];
  options: AccountOption[];
  disabled?: boolean;
  placeholder?: string;
  onChange: (newValues: string[]) => void;
};

const UNKNOWN_INSTITUTION = "__unknown__";

const accountLabel = (account: AccountOption): string => {
  const ibanTail = account.iban ? `••${account.iban.slice(-4)}` : null;
  if (account.accountName && ibanTail) {
    return `${account.accountName} (${ibanTail})`;
  }
  return account.accountName || ibanTail || account.externalAccountId;
};

/**
 * Combined institution / account multi-select for the Verification View.
 * Accounts are grouped under their institution: ticking an institution selects
 * (or clears) all of its accounts, and individual accounts can be toggled. The
 * selection is a flat list of `external_account_id`s applied on close.
 *
 * SMT-1191 moved the grouping behaviour itself into GroupedFilterDropdown so the
 * Receipt Overview source filter can reuse it; this is now only the
 * bank-account-shaped mapping on top of it.
 */
export default function AccountFilterDropdown({
  values,
  options,
  disabled = false,
  placeholder,
  onChange,
}: Props) {
  const entries = useMemo<GroupedFilterEntry[]>(() => {
    const byInstitution = new Map<
      string,
      { institutionName: string; accounts: AccountOption[] }
    >();
    options.forEach((option) => {
      // Distinguish accounts that lack an institutionId by their name, so two
      // genuinely different unknown institutions don't merge into one group.
      const key =
        option.institutionId ??
        `${UNKNOWN_INSTITUTION}:${option.institutionName ?? ""}`;
      if (!byInstitution.has(key)) {
        byInstitution.set(key, {
          institutionName: option.institutionName ?? "Unknown bank",
          accounts: [],
        });
      }
      byInstitution.get(key)?.accounts.push(option);
    });

    return Array.from(byInstitution.entries()).map(([key, group]) => ({
      kind: "group" as const,
      key,
      label: group.institutionName,
      options: group.accounts.map((account) => ({
        value: account.externalAccountId,
        label: accountLabel(account),
      })),
    }));
  }, [options]);

  return (
    <GroupedFilterDropdown
      id="account-dropdown"
      values={values}
      entries={entries}
      disabled={disabled}
      placeholder={placeholder || "All accounts"}
      emptyText="No connected accounts"
      /*
       * Pinned to the 200px this filter has always used, so extracting the shared
       * component does not silently resize the Verification View. The taller
       * grouped default is for the Receipt Overview, which stacks far more rows.
       */
      maxHeight={200}
      onChange={onChange}
    />
  );
}
